import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, throwError } from 'rxjs';
import { NotificationService } from '../services/notification.service';
import { getApiErrorMessage } from '../utils/error.utils';

/**
 * Global HTTP error interceptor.
 *
 * Responsibilities:
 * - Normalize HttpErrorResponse instances.
 * - Trigger a generic user-friendly notification only for truly unexpected errors
 *   (network errors and 5xx server errors).
 * - For 4xx errors, let components handle notifications with context-specific messages.
 * - Rethrow the error so feature code can still react if needed.
 */
export const httpErrorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const notificationService = inject(NotificationService);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {
        const status = error.status;

        // Network error / no response - show notification automatically
        if (status === 0) {
          notificationService.showError(
            'We could not reach the server. Please check your internet connection and try again.'
          );
        } else if (status >= 500) {
          // Server-side error - show notification automatically
          notificationService.showError(
            'Something went wrong on our side. Please try again in a moment.'
          );
        }
        // For 4xx errors (400-499), don't show automatic notifications.
        // Components handle these with context-specific messages to avoid duplicates.
      }

      // Always rethrow so callers can handle specific cases if they need to.
      return throwError(() => error);
    })
  );
};

